import { useEffect, useState } from 'react';
import { endOfDay, startOfDay } from 'date-fns';
import { useQuery } from '@apollo/client';
import { Button, Flex, StyledDatePicker, StyledFormSelect, WrappedQuery } from '../';
import {
  baseHeader,
  schoolPromotionBaseColumn,
  onlyInterventionHeader,
  onlyProgrammeHeader,
  onlyServiceProviderHeader,
} from '../../../consts';
import {
  ALL_INTERVENTIONS,
  ALL_SERVICE_PROVIDERS,
  EXPORT_PROMOTION_SCHOOLS,
  EXPORT_SCHOOL_PROMOTION,
} from '../../../graphql';
import { formatSchoolExportData, formatSelectValue, getPortal, sanitizeData } from '../../../utils';
import CSVLink from '../CSVLink';
import { getAllProgrammesQuery } from '../../../containers/shared/programmes/utils';
import { OperationVariables, QueryResult } from '@apollo/client';

type State = {
  programmeIds?: string[];
  serviceProviderIds?: string[];
  interventionIds?: string[];
  schoolIds?: string[];
};

const headers = [...baseHeader, ...schoolPromotionBaseColumn];
const onlyIntervention = [...onlyInterventionHeader, ...schoolPromotionBaseColumn];
const onlyProgrammes = [...onlyProgrammeHeader, ...schoolPromotionBaseColumn];
const onlyServiceProvider = [...onlyServiceProviderHeader, ...schoolPromotionBaseColumn];

const SchoolPromotionExport = () => {
  const [portal] = useState(getPortal());
  const [state, setState] = useState<State>({});
  const [institutions, setInstitutions] = useState<[{ node: { id: string } }] | any>([]);
  const [enrolmentData, setData] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const { data: programmeData, error: programmeError, loading: programmeDataLoading } = useQuery(
    getAllProgrammesQuery(portal),
    {
      variables: {
        input: {
          isArchived: false,
          includeWhereReporter: true,
        },
      },
    }
  );
  const { data: spData, error: spError, loading: spDataLoading } = useQuery(ALL_SERVICE_PROVIDERS, {
    variables: {
      input: {
        ...(state.programmeIds && { programmeIds: state.programmeIds }),
      },
    },
  });
  const { data: interventionData, error: interventionError, loading: interventionDataLoading } = useQuery(
    ALL_INTERVENTIONS,
    {
      variables: {
        input: {
          ...(state.programmeIds && { programmeIds: state.programmeIds }),
          ...(state.serviceProviderIds && { serviceProviderIds: state.serviceProviderIds }),
        },
      },
    }
  );
  const {
    data: promotionSchoolsData,
    fetchMore: fetchMoreInstitutions,
    error: promotionSchoolsError,
    loading: promotionSchoolsLoading,
  } = useQuery(EXPORT_PROMOTION_SCHOOLS, {
    variables: {
      input: {
        first: 10,
        ...(state.interventionIds && { interventionIds: state.interventionIds }),
        ...(state.serviceProviderIds && { serviceProviderIds: state.serviceProviderIds }),
        ...(state.programmeIds && { programmeIds: state.programmeIds }),
      },
    },
  });

  useEffect(() => {
    if (promotionSchoolsData && promotionSchoolsData.institutions && promotionSchoolsData.institutions.edges) {
      setInstitutions(promotionSchoolsData.institutions.edges);
    }
  }, [promotionSchoolsData]);

  return (
    <Flex size="1" direction="column">
      <Flex size="1" direction="row" marginRight="10px">
        <StyledFormSelect
          isMulti={true}
          isClearable={true}
          name="programmeIds"
          label="Programme"
          placeholder={programmeDataLoading ? 'Data loading, Please wait' : 'Data ready, Please select option'}
          isDisabled={programmeDataLoading}
          onChange={(option: any[]) => {
            const newState = Object.assign({}, state, {
              programmeIds: option ? option.map(opt => opt.value) : undefined,
            });
            setState(newState);
          }}
          options={
            programmeDataLoading || programmeError || !(programmeData && programmeData.programmes)
              ? []
              : programmeData.programmes.map((item: any) =>
                  formatSelectValue({
                    label: item.name,
                    value: item.id,
                  })
                )
          }
        />
      </Flex>
      <Flex size="1" direction="row" marginRight="10px">
        <StyledFormSelect
          isMulti={true}
          isClearable={true}
          name="serviceProviderIds"
          label="Service Provider"
          placeholder={spDataLoading ? 'Data loading, Please wait' : 'Data ready, Please select option'}
          isDisabled={spDataLoading}
          onChange={(option: any[]) => {
            const newState = Object.assign({}, state, {
              serviceProviderIds: option ? option.map(opt => opt.value) : undefined,
            });
            setState(newState);
          }}
          options={
            spDataLoading || spError || !(spData && spData.serviceProviders)
              ? []
              : spData.serviceProviders.map((item: any) =>
                  formatSelectValue({
                    label: item.name,
                    value: item.id,
                  })
                )
          }
        />
      </Flex>
      <Flex size="1" direction="row" marginRight="10px">
        <StyledFormSelect
          isMulti={true}
          isClearable={true}
          name="interventionIds"
          label="Intervention"
          placeholder={interventionDataLoading ? 'Data loading, Please wait' : 'Data ready, Please select option'}
          isDisabled={interventionDataLoading}
          onChange={(option: any[]) => {
            const newState = Object.assign({}, state, {
              interventionIds: option ? option.map(opt => opt.value) : undefined,
            });
            setState(newState);
          }}
          options={
            interventionDataLoading || interventionError || !(interventionData && interventionData.allInterventions)
              ? []
              : interventionData.allInterventions.map((item: any) =>
                  formatSelectValue({
                    label: item.name,
                    value: item.id,
                  })
                )
          }
        />
      </Flex>
      <Flex size="1.2" direction="column">
        <StyledDatePicker
          label="Intervention Date"
          name={`interventionDate`}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
        />
      </Flex>
      <Flex size="1" direction="row" marginRight="10px">
        <StyledFormSelect
          isMulti={true}
          isClearable={true}
          name="schoolIds"
          label="School"
          placeholder={promotionSchoolsLoading ? 'Data loading, Please wait' : 'Data ready, Please select option'}
          isDisabled={promotionSchoolsLoading}
          onChange={(option: any[]) => {
            const newState = Object.assign({}, state, {
              schoolIds: option ? option.map(opt => opt.value) : undefined,
            });
            setState(newState);
          }}
          options={
            promotionSchoolsLoading ||
            promotionSchoolsError ||
            !(promotionSchoolsData && promotionSchoolsData.institutions)
              ? []
              : promotionSchoolsData.institutions.edges.map((item: any) =>
                  formatSelectValue({
                    label: item.node.name,
                    value: item.node.id,
                  })
                )
          }
          scrollHandler={async () => {
            // call the next page of the filteredInsitutions query
            return (
              promotionSchoolsData.institutions &&
              promotionSchoolsData.institutions.pageInfo &&
              promotionSchoolsData.institutions.pageInfo.hasNextPage &&
              fetchMoreInstitutions({
                variables: {
                  input: {
                    first: 10,
                    skip: institutions.length,
                    ...(state.interventionIds && { interventionIds: state.interventionIds }),
                    ...(state.serviceProviderIds && { serviceProviderIds: state.serviceProviderIds }),
                    ...(state.programmeIds && { programmeIds: state.programmeIds }),
                  },
                },
              }).then(({ data }: any) => {
                if (data && data.institutions && data.institutions.edges) {
                  const newInstitutions = [...institutions, ...data.institutions.edges];
                  setInstitutions(newInstitutions);
                }
              })
            );
          }}
        />
      </Flex>
      <WrappedQuery
        query={EXPORT_SCHOOL_PROMOTION}
        variables={{
          input: {
            ...(state.schoolIds && { schoolIds: state.schoolIds }),
            ...(state.interventionIds && { interventionIds: state.interventionIds }),
            ...(state.serviceProviderIds && { serviceProviderIds: state.serviceProviderIds }),
            ...(state.programmeIds && { programmeIds: state.programmeIds }),
            ...(startDate && { startDate: startOfDay(new Date(startDate)) }),
            ...(endDate && { endDate: endOfDay(new Date(endDate)) }),
          },
        }}
        skip={!enrolmentData}
      >
        {({ loading, data }: QueryResult<any, OperationVariables>) => {
          if (loading) {
            return <div>Preparing data for export...</div>;
          }

          if (!data) {
            return <Button type="button" text="Generate Export" mr={3} onClick={() => setData(true)} />;
          }

          const { programmeIds, serviceProviderIds, interventionIds, schoolIds } = state;

          let exportHeaders;

          if (interventionIds !== undefined) {
            exportHeaders = onlyIntervention;
          } else if (serviceProviderIds !== undefined) {
            exportHeaders = onlyServiceProvider;
          } else if (programmeIds !== undefined) {
            exportHeaders = onlyProgrammes;
          } else {
            exportHeaders = headers;
          }

          const formatted: any =
            data && data.exportSchoolPromotion
              ? formatSchoolExportData({
                  schoolDataType: 'School Promotion Data',
                  data: data && data.exportSchoolPromotion,
                  interventionIds,
                  schoolIds,
                })
              : [];

          const sanitized = formatted.length ? sanitizeData(formatted) : [];

          return (
            <Flex size="1" direction="row" marginRight="10px">
              {data && data.exportSchoolPromotion && (
                <CSVLink headers={exportHeaders} filename={`SchoolPromotionExport-${new Date()}.csv`} data={sanitized}>
                  Export CSV
                </CSVLink>
              )}
            </Flex>
          );
        }}
      </WrappedQuery>
    </Flex>
  );
};

export default SchoolPromotionExport;
